/**
 * Bundle Monitor
 * Überwacht Bundle-Integrität im Warenkorb und entfernt automatisch ungültige Bundles
 */

class BundleMonitor {
  constructor() {
    this.isProcessing = false;
    this.init();
  }
  
  init() {
    debugLog('🔍 Bundle Monitor initialisiert');
    
    // Monitor cart changes
    this.bindCartEvents();
    
    // Check on page load
    this.checkBundlesOnLoad();
  }
  
  bindCartEvents() {
    // Listen to cart updates
    document.addEventListener('cart:updated', () => {
      debugLog('🛒 Warenkorb aktualisiert - prüfe Bundles...');
      setTimeout(() => this.validateBundles(), 500);
    });
    
    // Listen to cart refresh (after render)
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.enhanceCartDisplay(), 300);
    });
    
    // Listen to line item quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('quantity__input') || 
          e.target.name === 'updates[]') {
        debugLog('📦 Mengenänderung erkannt - prüfe Bundles...');
        setTimeout(() => this.validateBundles(), 500);
      }
    });
    
    // Listen to line item removals
    document.addEventListener('click', (e) => {
      if (e.target.closest('.line-item__remove') || 
          e.target.closest('[data-action="remove-line-item"]')) {
        debugLog('🗑️ Produkt wird entfernt - prüfe Bundles...');
        setTimeout(() => this.validateBundles(), 800);
      }
    });
  }
  
  async checkBundlesOnLoad() {
    // Wait for page to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validateBundles());
    } else {
      await this.validateBundles();
    }
  }
  
  async validateBundles() {
    if (this.isProcessing) {
      debugLog('⏳ Bundle-Validierung läuft bereits...');
      return;
    }
    
    this.isProcessing = true;
    
    try {
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (!cart || !cart.items || cart.items.length === 0) {
        debugLog('📭 Warenkorb ist leer');
        this.isProcessing = false;
        return;
      }
      
      // Find all bundle items
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id
      );
      
      if (bundleItems.length === 0) {
        debugLog('✅ Keine Bundle-Produkte im Warenkorb');
        this.isProcessing = false;
        return;
      }
      
      // Group by bundle ID
      const bundles = {};
      bundleItems.forEach(item => {
        const bundleId = item.properties._bundle_id;
        if (!bundles[bundleId]) {
          bundles[bundleId] = {
            id: bundleId,
            items: [],
            expectedCount: parseInt(item.properties._bundle_count) || 0,
            discount: parseInt(item.properties._bundle_discount) || 0
          };
        }
        bundles[bundleId].items.push(item);
      });
      
      // Check each bundle for integrity
      const invalidBundles = [];
      
      Object.values(bundles).forEach(bundle => {
        const actualCount = bundle.items.length;
        const expectedCount = bundle.expectedCount;
        
        debugLog(`📦 Bundle ${bundle.id.substring(0, 15)}... - Erwartet: ${expectedCount}, Vorhanden: ${actualCount}`);
        
        if (actualCount !== expectedCount) {
          debugLog(`❌ Bundle ${bundle.id} ist ungültig!`);
          invalidBundles.push(bundle);
        } else {
          debugLog(`✅ Bundle ${bundle.id} ist vollständig`);
        }
      });
      
      // Remove invalid bundles
      if (invalidBundles.length > 0) {
        await this.removeInvalidBundles(invalidBundles);
      }
      
      // Enhance cart display
      this.enhanceCartDisplay();
      
    } catch (error) {
      debugError('❌ Fehler bei Bundle-Validierung:', error);
    } finally {
      this.isProcessing = false;
    }
  }
  
  async removeInvalidBundles(invalidBundles) {
    debugLog(`🗑️ Entferne ${invalidBundles.length} ungültige Bundle(s)...`);
    
    try {
      // Collect all line item keys to remove
      const itemsToRemove = [];
      
      invalidBundles.forEach(bundle => {
        bundle.items.forEach(item => {
          itemsToRemove.push({
            id: item.key,
            quantity: 0
          });
        });
      });
      
      // Remove items via cart update
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          updates: Object.fromEntries(itemsToRemove.map(item => [item.id, 0]))
        })
      });
      
      if (response.ok) {
        debugLog('✅ Ungültige Bundle-Produkte entfernt');
        
        // Show notification
        this.showNotification(
          'Ein Bundle wurde aus dem Warenkorb entfernt, da nicht alle Produkte vorhanden waren.',
          'info'
        );
        
        // Trigger cart refresh
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Failed to remove bundle items');
      }
      
    } catch (error) {
      debugError('❌ Fehler beim Entfernen ungültiger Bundles:', error);
    }
  }
  
  enhanceCartDisplay() {
    // Find all line items with bundle properties
    const lineItems = document.querySelectorAll('.line-item, .mini-cart__item, .cart-item');
    
    lineItems.forEach(lineItem => {
      // Check if this item has bundle properties
      const bundleProperties = lineItem.querySelector('[class*="property"]');
      if (!bundleProperties) return;
      
      // Suche nach neuer Rabatt-Property (mit Emoji)
      const bundleRabattText = Array.from(bundleProperties.querySelectorAll('li')).find(li => 
        li.textContent.includes('🎁 Rabatt') || li.textContent.includes('Rabatt:')
      );
      
      if (bundleRabattText) {
        // Add bundle badge if not already present
        if (!lineItem.querySelector('.bundle-badge')) {
          this.addBundleBadge(lineItem);
        }
        
        // Enhance price display
        this.enhancePriceDisplay(lineItem);
      }
    });
    
    // Add bundle summary if bundles exist
    this.addBundleSummary();
  }
  
  addBundleBadge(lineItem) {
    const productTitle = lineItem.querySelector('.product-item-meta__title, .line-item__title, h3');
    if (!productTitle) return;
    
    const badge = document.createElement('span');
    badge.className = 'bundle-badge';
    badge.innerHTML = '🎁 Bundle';
    badge.style.cssText = `
      display: inline-block;
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.7rem;
      font-weight: 600;
      margin-left: 8px;
      vertical-align: middle;
    `;
    
    productTitle.appendChild(badge);
  }
  
  enhancePriceDisplay(lineItem) {
    // Find price display element
    const priceElement = lineItem.querySelector('.price, .line-item__price');
    if (!priceElement) return;
    
    // Check if already enhanced
    if (priceElement.querySelector('.bundle-discounted-price')) return;
    
    // Get original price
    const originalPriceElement = priceElement.querySelector('.price, span');
    if (!originalPriceElement) return;
    
    const originalPrice = originalPriceElement.textContent.trim();
    
    // Try to calculate discounted price from properties
    // Look for hidden _discounted_price property in data attributes or text
    let discountedPrice = null;
    
    // Check if we can find the discount percentage from the visible property
    const rabattProperty = lineItem.querySelector('li');
    if (rabattProperty) {
      const match = rabattProperty.textContent.match(/(\d+)%/);
      if (match) {
        const discountPercent = parseInt(match[1]);
        // Extract price value
        const priceMatch = originalPrice.match(/[\d,]+/);
        if (priceMatch) {
          const priceValue = parseFloat(priceMatch[0].replace(',', '.'));
          const discountedValue = priceValue * (1 - discountPercent / 100);
          discountedPrice = `€${discountedValue.toFixed(2).replace('.', ',')}`;
        }
      }
    }
    
    if (!discountedPrice) return;
    
    // Create enhanced price display
    const enhancedPrice = document.createElement('div');
    enhancedPrice.className = 'bundle-price-display';
    enhancedPrice.innerHTML = `
      <span class="original-price-strike" style="
        text-decoration: line-through;
        color: #9ca3af;
        font-size: 0.9em;
        display: block;
        margin-bottom: 2px;
      ">${originalPrice}</span>
      <span class="bundle-discounted-price" style="
        color: #10b981;
        font-weight: 600;
        font-size: 1.1em;
      ">${discountedPrice}</span>
    `;
    
    // Replace original price display
    priceElement.innerHTML = '';
    priceElement.appendChild(enhancedPrice);
  }
  
  async addBundleSummary() {
    // Get current cart to calculate bundle savings
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id
      );
      
      if (bundleItems.length === 0) return;
      
      // Calculate total savings
      let totalSavings = 0;
      bundleItems.forEach(item => {
        const originalPrice = parseInt(item.properties._original_price) || item.price;
        const discountedPrice = parseInt(item.properties._discounted_price) || item.price;
        totalSavings += (originalPrice - discountedPrice) * item.quantity;
      });
      
      if (totalSavings <= 0) return;
      
      // Find cart footer or summary area
      const cartFooter = document.querySelector('.drawer__footer, .cart__footer, .cart-summary');
      if (!cartFooter) return;
      
      // Check if summary already exists
      if (cartFooter.querySelector('.bundle-savings-summary')) return;
      
      // Create bundle savings summary
      const savingsSummary = document.createElement('div');
      savingsSummary.className = 'bundle-savings-summary';
      savingsSummary.style.cssText = `
        background: linear-gradient(135deg, #10b981, #059669);
        color: white;
        padding: 12px 16px;
        margin: 12px 0;
        border-radius: 8px;
        text-align: center;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
      `;
      savingsSummary.innerHTML = `
        <div style="font-size: 0.85rem; opacity: 0.9; margin-bottom: 4px;">
          🎉 Bundle-Ersparnis
        </div>
        <div style="font-size: 1.2rem; font-weight: 700;">
          ${this.formatMoney(totalSavings)}
        </div>
      `;
      
      // Insert before checkout button
      const checkoutButton = cartFooter.querySelector('.cart__checkout-button, button[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.parentNode.insertBefore(savingsSummary, checkoutButton);
      } else {
        cartFooter.insertBefore(savingsSummary, cartFooter.firstChild);
      }
      
    } catch (error) {
      debugError('❌ Fehler beim Erstellen der Bundle-Zusammenfassung:', error);
    }
  }
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return amount.replace('.', ',') + ' €';
  }
  
  showNotification(message, type = 'info', duration = 5000) {
    // Remove existing notification
    const existingNotification = document.querySelector('.bundle-monitor-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bundle-monitor-notification bundle-monitor-notification--${type}`;
    notification.innerHTML = `
      <div class="bundle-monitor-notification__content">
        <span class="bundle-monitor-notification__message">${message}</span>
        <button type="button" class="bundle-monitor-notification__close">✕</button>
      </div>
    `;
    
    // Add styles
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      max-width: 400px;
      animation: slideInRight 0.3s ease;
    `;
    
    // Add to DOM
    document.body.appendChild(notification);
    
    // Auto remove after specified duration
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => notification.remove(), 300);
      }
    }, duration);
    
    // Manual close
    const closeButton = notification.querySelector('.bundle-monitor-notification__close');
    closeButton.addEventListener('click', () => {
      notification.style.animation = 'slideOutRight 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    });
  }
}

// Initialize bundle monitor when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BundleMonitor = new BundleMonitor();
  });
} else {
  window.BundleMonitor = new BundleMonitor();
}

// Export for use in other scripts
window.BundleMonitor = BundleMonitor;
